import { listsV2 } from '@ecosystem/newron-design-system';
import React, { useCallback, useMemo, useState } from 'react';

import { commonTableCellConfig } from '../../../utils/constants';
import { sortByKey } from '../../../utils/helper';
import { ColumnConfiguration, TableRow } from '../../types';
import { getColumnValue } from '../../utils';
import { getColumnSortingValue } from './utils';
import { DefaultTabBodyProps } from './types';

const { Table } = listsV2;

const getColumnConfig = (columnConfig: ColumnConfiguration[] = []): listsV2.ColumnConfig[] =>
  columnConfig.map(
    (col: ColumnConfiguration): listsV2.ColumnConfig => ({
      ...col,
      subComponent: (columnValue: TableRow[keyof TableRow]) => <>{getColumnValue(columnValue)}</>,
      cellConfig: commonTableCellConfig,
    })
  );

export const DefaultContent: React.FC<DefaultTabBodyProps> = ({
  tableInfo,
  selectedRows,
  onRowSelection,
  rowSelectionMode,
  disableSelectAllButton,
}) => {
  // State for sorting and table data
  const [sortingState, setSortingState] = useState<listsV2.SortingChangeProps>();

  // Prepare and memoize columns with custom cell styles
  const columnConfig = useMemo<listsV2.ColumnConfig[]>(
    () => getColumnConfig(tableInfo.columnConfig),
    [tableInfo.columnConfig]
  );

  // Stable selector cell config to avoid re-renders
  const selectorCellConfig = useMemo(() => ({ hasBottomBorder: false, hasRightBorder: false }), []);

  // Handle sorting changes
  const onSortingChange = useCallback(({ id, sortingOrder }: { id: string; sortingOrder: listsV2.SortingOrder }) => {
    setSortingState(sortingOrder === 'none' ? undefined : { id, sortingOrder });
  }, []);

  // Derive view data (sorted or raw).
  const tableData = useMemo<TableRow[]>(() => {
    const data = tableInfo.data ?? [];
    if (!sortingState) return data;
    const getSortingValue = (row: TableRow, columnKey: keyof TableRow) => getColumnSortingValue(row, columnKey);
    return sortByKey(data, sortingState, getSortingValue);
  }, [tableInfo.data, sortingState]);

  return (
    <Table
      data={tableData}
      columnConfig={columnConfig}
      isGroupingEnabled={false}
      selectorCellConfig={selectorCellConfig}
      isRowSelectable
      rowSelectionMode={rowSelectionMode}
      selectedRows={selectedRows}
      onRowSelectionChange={onRowSelection}
      sortingState={sortingState}
      onSortingChange={onSortingChange}
      disableSelectAllButton={disableSelectAllButton}
    />
  );
};
